// src/utils/csvExtractors.js
import dataStore from "./dataStore";

/* helpers ──────────────────────────────────────────────────────── */
const normalize = s => s.toLowerCase().replace(/\s+/g, " ").trim();

/* virgül korumalı anahtar-değer ayırıcı */
const kvPairs = (v = "") => {
  const out = [];
  v.split(",").forEach(tok => {
    const i = tok.indexOf(":");
    if (i > -1) {
      out.push([tok.slice(0, i).trim().toLowerCase(), tok.slice(i + 1).trim()]);
    } else if (out.length) {
      // Önceki anahtar-değer çiftine virgüllü fragment ekle
      out[out.length - 1][1] += `,${tok.trim()}`;
    }
  });
  return out;
};

const dims = (a, b) => `${a.toUpperCase()}X${b.toUpperCase()}`;
const cap  = s => (s ? s[0].toUpperCase() + s.slice(1) : "");

const styleKeywords = () =>
  Object.keys({ ...dataStore.apparelStyles, ...dataStore.printStyles }).sort(
    (a, b) => b.length - a.length
  );

/* ───────── STYLE ───────── */
export function extractStyle(item, vars) {
  const kws = styleKeywords();
  const txt  = normalize(`${item} ${vars}`); // örneğin: "toddler 4t, ketchup sticker, ..."

  // 🔴 ÖNCE “nutella” ve “ketchup” özel durumlarını yakala
  if (txt.includes("nutella")) {
    return "Nutella Sticker";
  }
  if (txt.includes("ketchup")) {
    return "Ketchup Sticker";
  }

  /* 1️⃣  Variations › SIZE önceliği (sadece Apparel sözlüğünde varsa) */
  for (const [k, v] of kvPairs(vars)) {
    if (!k.includes("size")) continue;

    const raw = v.replace(/\s+/g, " ").trim(); // “Toddler 4T” vb.

    // a) “Hoodie / L” kalıbı
    if (raw.includes("/")) {
      const left = normalize(raw.split("/")[0]);
      const hit  = kws.find(
        kw => left.includes(kw) && dataStore.apparelStyles[kw]
      );
      if (hit) return dataStore.apparelStyles[hit];
    }

    // b) “Toddler 4T” veya “3-6 Month Onesie”
    const words = normalize(raw).split(" ");
    const first = words[0];
    const last  = words[words.length - 1];

    const tryWord = w =>
      dataStore.apparelStyles[w] ? dataStore.apparelStyles[w] : null;

    if (tryWord(first)) return tryWord(first);
    if (tryWord(last))  return tryWord(last);
  }

  /* 2️⃣  Eski mantık (Apparel + Print) */
  // styleKeywords'teki en uzun anahtarları önce kontrol ediyoruz
  let hit = kws.find(kw => txt.includes(kw));
  if (hit) return dataStore.apparelStyles[hit] || dataStore.printStyles[hit];

  // Çok kelimeli anahtarları incele
  for (const kw of kws.filter(k => k.includes(" "))) {
    if (kw.split(" ").every(w => txt.includes(w))) {
      return dataStore.apparelStyles[kw] || dataStore.printStyles[kw];
    }
  }

  /* 3️⃣  “X Sticker” veya “X Label” gibi genel regex */
  const m = txt.match(/(\b\w+\b)\s+(sticker|label)/);
  if (m) return `${cap(m[1])} ${cap(m[2])}`;

  return null;
}

/* ───────── CATEGORY ───────── */
export function extractCategory(style, item, engr) {
  const sku = item["SKU"]?.toLowerCase() || "";
  if (engr.some(p => p.sku?.toLowerCase() === sku)) return "Engraving";
  return style && Object.values(dataStore.apparelStyles).includes(style)
    ? "Apparel"
    : style
    ? "Print"
    : null;
}

export function extractCategoryFromShipStations(style, item, engr) {
  const sku = item["OrderItemSku"]?.toLowerCase() || "";
  if (engr.some(p => p.sku?.toLowerCase() === sku)) return "Engraving";
  return style && Object.values(dataStore.apparelStyles).includes(style)
    ? "Apparel"
    : style
    ? "Print"
    : null;
}

/* ───────── COLOR ───────── */
export function extractColor(item, vars) {
  // Variations içinden “color” ile başlayan anahtar
  for (const [k, v] of kvPairs(vars)) {
    if (k.startsWith("color")) {
      return cap(v.replace(/\s+/g, " "));
    }
  }

  // “frame”, “border” veya “edge” içeren anahtar
  for (const [k, v] of kvPairs(vars)) {
    if (["frame", "border", "edge"].some(tag => k.includes(tag))) {
      return cap(v.replace(/\s+/g, " "));
    }
  }

  // dataStore.colors paletinde eşleşme ara
  const palette = dataStore.colors.map(c => c.toLowerCase());
  for (const [, v] of kvPairs(vars)) {
    const hit = palette.find(c => normalize(v).includes(c));
    if (hit) return cap(hit);
  }

  // item ve vars birleşim metninde renk araması
  const txt = normalize(`${item} ${vars}`);
  const hit = palette.find(c => txt.includes(c));
  return hit ? cap(hit) : null;
}

/* ───────── SIZE ───────── */
export function extractSize(item, vars, style) {
  if (style === "Yard Sign") {
    const t = normalize(vars);
    if (t.includes("double side")) return "Double Side";
    if (t.includes("single side")) return "Single Side";
  }

  // Shape kontrolü
  for (const [k, v] of kvPairs(vars)) {
    if (k.includes("shape")) {
      const hit = dataStore.shapeSizes.find(s => normalize(v) === s);
      if (hit) return cap(hit);
    }
  }

  // “size” veya “dimension” anahtarları
  for (const [k, v] of kvPairs(vars)) {
    if (k.includes("size") || k.includes("dimension")) {
      let val = v.split("/").pop(); // “Hoodie / L” → “L”

      // Eğer style metni içeriyorsa, örneğin “3-6 Month Onesie”
      if (style && val.toLowerCase().includes(style.toLowerCase())) {
        val = val.replace(new RegExp(style, "i"), "").trim();
      }

      const out = parseSize(val);
      if (out) return out;
      if (val.trim()) return cap(val).slice(0, 30);
    }
  }

  // Son çare: item + vars birleşiminden parse et
  return parseSize(`${item} ${vars}`);
}

function parseSize(raw) {
  const txt = normalize(raw);

  // -------------- Ölçü formatı (örn. 12x18 veya 12×18 veya 12*18)
  const dec = txt.match(/(\d+(?:[.,]\d+)?)\s*[x×*]\s*(\d+(?:[.,]\d+)?)/);
  if (dec) return dims(dec[1].replace(".", ","), dec[2].replace(".", ","));

  // -------------- Çocuk bedenleri (örn. “4T”, “2T”)
  const toddler = txt.match(/\b\d+t\b/);
  if (toddler) return toddler[0].toUpperCase();

  // -------------- Ons (örn. “16 oz”)
  const oz = txt.match(/(\d+)\s*oz/);
  if (oz) return `${oz[1]} OZ`;

  // -------------- Ay aralığı (örn. “3 - 6 month”)
  const mon = txt.match(/(\d+\s*-\s*\d+)\s*month/);
  if (mon) return `${mon[1].replace(/\s+/g, "")} Month`;

  // -------------- XS, S, M, L, XL, 2XL, ...
  const let_ = txt.match(/\b(xs|s|m|l|xl|2xl|3xl|4xl|5xl)\b/);
  if (let_) return let_[1].toUpperCase();

  // -------------- dataStore.shapeSizes içinde eşleşme
  const shape = dataStore.shapeSizes.find(s => txt.includes(s));
  return shape ? cap(shape) : null;
}
